// client/src/pages/admin/UserManagement.jsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Alert,
  Chip,
  Tooltip,
  Grid,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  SportsEsports as CharacterIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import Gateway from '../../api/gateway';
import RoleBadge from '../../components/admin/RoleBadge';
import RoleChangeModal from '../../components/admin/RoleChangeModal';
import UserPermissionsModal from '../../components/admin/UserPermissionsModal';

// ============================================================================
// Константы
// ============================================================================

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'Все роли' },
  { value: 'user', label: 'Игрок' },
  { value: 'curator', label: 'Куратор' },
  { value: 'admin', label: 'Администратор' },
  { value: 'superadmin', label: 'Суперадминистратор' },
];

const SORT_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'role', label: 'Роль' },
  { value: 'created_at', label: 'Дата создания' },
];

const TAB_PLAYERS = 0;
const TAB_AUDIT = 1;

// ============================================================================
// Форматирование даты
// ============================================================================

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ============================================================================
// Вкладка «Игроки»
// ============================================================================

function PlayersTab() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [filters, setFilters] = useState({ q: '', role: '', sort: 'email' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);

  const fetchUsers = useCallback(
    async (page = 1, limit = meta.limit || 20) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page, limit };
        if (filters.q?.trim()) params.q = filters.q.trim();
        if (filters.role) params.role = filters.role;
        if (filters.sort) params.sort = filters.sort;

        const res = await Gateway.api.accessAdmin.listUsers(params);

        if (res?.success && Array.isArray(res.data)) {
          setUsers(res.data);
          setMeta(
            res.meta || { page, limit, total: res.data.length, total_pages: 1 }
          );
        } else {
          setUsers([]);
          setMeta({ page, limit, total: 0, total_pages: 0 });
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          setError('Недостаточно прав. Требуется роль admin или superadmin.');
        } else {
          setError(err.response?.data?.error || err.message || 'Ошибка загрузки');
        }
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, meta.limit]
  );

  useEffect(() => {
    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchUsers(1);

  const handleReset = () => {
    setFilters({ q: '', role: '', sort: 'email' });
    setTimeout(() => fetchUsers(1), 0);
  };

  const handlePageChange = (_, newPage) => {
    fetchUsers(newPage + 1);
  };

  const handleRowsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    fetchUsers(1, newLimit);
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Поиск по email / username / display_name"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Роль"
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            >
              {ROLE_FILTER_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Сортировка"
              value={filters.sort}
              onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                Искать
              </Button>
              <Button onClick={handleReset} disabled={loading}>
                Сбросить
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => fetchUsers(meta.page || 1)}
                disabled={loading}
              >
                Обновить
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Пользователь</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell align="right">Персонажи</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Загрузка…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Пользователи не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.kratos_identity_id || u.player_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {u.display_name || '—'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {(u.kratos_identity_id || '').substring(0, 8) || '—'}…
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{u.email || '—'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <RoleBadge role={u.system_role} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Количество персонажей">
                        <Chip
                          icon={<CharacterIcon />}
                          label={u.characters_count ?? 0}
                          size="small"
                          variant="outlined"
                          color={(u.characters_count ?? 0) > 0 ? 'primary' : 'default'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={u.created_at || ''}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatDate(u.created_at)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button
                          size="small"
                          startIcon={<AdminIcon />}
                          onClick={() => setSelectedUser(u)}
                        >
                          Роль
                        </Button>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => setPermissionsUser(u)}
                        >
                          Права
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={meta.total || 0}
          page={Math.max(0, (meta.page || 1) - 1)}
          rowsPerPage={meta.limit || 20}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Строк:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} из ${count !== -1 ? count : '?'}`
          }
        />
      </Paper>

      {selectedUser && (
        <RoleChangeModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSuccess={() => fetchUsers(meta.page || 1)}
        />
      )}

      {permissionsUser && (
        <UserPermissionsModal
          user={permissionsUser}
          onClose={() => setPermissionsUser(null)}
        />
      )}
    </Box>
  );
}

// ============================================================================
// Вкладка «Audit log»
// ============================================================================

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
  const [filter, setFilter] = useState({ action: 'role_change' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(
    async (page = 1, limit = meta.limit || 50) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page, limit };
        if (filter.action) params.action = filter.action;

        const res = await Gateway.api.accessAdmin.listAudit(params);
        if (res?.success && Array.isArray(res.data)) {
          setLogs(res.data);
          setMeta(
            res.meta || { page, limit, total: res.data.length, total_pages: 1 }
          );
        } else {
          setLogs([]);
          setMeta({ page, limit, total: 0, total_pages: 0 });
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          setError('Недостаточно прав для просмотра audit-лога.');
        } else {
          setError(err.response?.data?.error || err.message || 'Ошибка загрузки');
        }
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [filter, meta.limit]
  );

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Action"
              value={filter.action}
              onChange={(e) => setFilter({ ...filter, action: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => fetchLogs(1)}
                disabled={loading}
              >
                Искать
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => fetchLogs(meta.page || 1)}
                disabled={loading}
              >
                Обновить
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Время</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Цель</TableCell>
                <TableCell>Было → Стало</TableCell>
                <TableCell>Кто</TableCell>
                <TableCell>Причина</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Записей нет
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => (
                  <TableRow key={`${log.timestamp || ''}-${idx}`} hover>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(log.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action || '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.target_email || log.target_kratos_id || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {log.old_role ? (
                          <RoleBadge role={log.old_role} textMode="short" showTooltip={false} />
                        ) : (
                          <Typography variant="caption">—</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          →
                        </Typography>
                        {log.new_role ? (
                          <RoleBadge role={log.new_role} textMode="short" showTooltip={false} />
                        ) : (
                          <Typography variant="caption">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {log.changed_by_email || log.changed_by || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {log.reason || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={meta.total || 0}
          page={Math.max(0, (meta.page || 1) - 1)}
          rowsPerPage={meta.limit || 50}
          onPageChange={(_, p) => fetchLogs(p + 1)}
          onRowsPerPageChange={(e) => fetchLogs(1, parseInt(e.target.value, 10))}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Строк:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} из ${count !== -1 ? count : '?'}`
          }
        />
      </Paper>
    </Box>
  );
}

// ============================================================================
// Основной компонент
// ============================================================================

export default function UserManagement() {
  const [tab, setTab] = useState(TAB_PLAYERS);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Управление пользователями
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Игроки, роли и audit-лог изменений
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Игроки и роли"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Audit-лог"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {tab === TAB_PLAYERS && <PlayersTab />}
      {tab === TAB_AUDIT && <AuditTab />}
    </Container>
  );
}
